import type { GameMode } from '../core/types/gameMode.types';

import type { EvaluationBlock, EvaluationExam } from '../core/types/payloads/evaluacion.types';

import { EXAM_BLOCK_LABELS } from '../core/types/payloads/evaluacion.types';

import {
  createBlockId,
  reorderBlocksPedagogically,
  validateExam,
} from './examOrchestrator';

import { validateCompletarPayload, parseCompletarBlanks } from '../activities/completar/completar.logic';

import { validateDesarrolloPayload } from '../activities/desarrollo/desarrollo.logic';

import { validateActivityPayload } from './pluginRunner';



export type ExamGenerateFocus = 'conceptual' | 'practical' | 'inquiry';



export interface ExamGenerateOptions {
  prompt: string;
  teacherId?: string;
  blockCount?: number;
  focus?: ExamGenerateFocus;
  subjectTag?: string;
  resourceType?: 'exam' | 'guide';
}

const GUIDE_FOCUS_MODE_SEQUENCE: Record<ExamGenerateFocus, GameMode[]> = {
  practical: ['info_block', 'completar', 'vincula', 'completar', 'agrupa', 'sandbox', 'desarrollo', 'completar'],
  conceptual: ['info_block', 'agrupa', 'completar', 'desarrollo', 'vincula', 'completar', 'sandbox', 'info_block'],
  inquiry: ['info_block', 'sandbox', 'order', 'completar', 'vincula', 'agrupa', 'desarrollo', 'completar'],
};



const FOCUS_MODE_SEQUENCE: Record<ExamGenerateFocus, GameMode[]> = {

  conceptual: ['completar', 'agrupa', 'desarrollo', 'vincula', 'completar', 'agrupa', 'desarrollo', 'vincula'],

  practical: ['arena', 'vincula', 'arena', 'vincula', 'arena', 'completar', 'vincula', 'arena'],

  inquiry: ['order', 'completar', 'sandbox', 'arena', 'order', 'vincula', 'agrupa', 'arena'],

};



function extractTopic(prompt: string): string {

  const cleaned = prompt

    .replace(/cr[ée]ame?\s+(una\s+)?(prueba|evaluaci[oó]n|examen)\s+(mixta\s+)?(de\s+)?/gi, '')

    .replace(/\d+\s*[íi]tems?/gi, '')

    .replace(/\d+\s*bloques?/gi, '')

    .replace(/sobre\s+/gi, '')

    .trim();

  const topic = cleaned.split(/[.,;]/)[0]?.trim();

  return topic && topic.length > 2 ? topic : 'Tema general';

}



function extractCount(prompt: string): number {

  const m = prompt.match(/(\d+)\s*[íi]tems?/i) || prompt.match(/(\d+)\s*bloques?/i);

  if (m) return Math.min(25, Math.max(2, Number(m[1])));

  const m2 = prompt.match(/\b([2-9]|1[0-9]|2[0-5])\b/);

  if (m2) return Number(m2[1]);

  return 8;

}



function pickModes(count: number, focus: ExamGenerateFocus = 'practical', resourceType: 'exam' | 'guide' = 'exam'): GameMode[] {
  const sequence =
    resourceType === 'guide' ? GUIDE_FOCUS_MODE_SEQUENCE[focus] : FOCUS_MODE_SEQUENCE[focus];
  return Array.from({ length: count }, (_, i) => sequence[i % sequence.length]!);
}



function blockForTopic(mode: GameMode, topic: string, index: number): EvaluationBlock {

  const label = `${EXAM_BLOCK_LABELS[mode] ?? mode} · ${topic}`;

  let payload: unknown;



  switch (mode) {
    case 'info_block':
      payload = validateActivityPayload('info_block', {
        title: `Contexto: ${topic}`,
        sectionTitle: 'Introducción',
        bodyText: `En esta guía exploraremos ${topic}: primero el contexto, luego actividades de práctica guiada.`,
        layout: 'context',
      });
      break;

    case 'arena':

      payload = validateActivityPayload('arena', {

        title: label,

        settings: { defaultTimeLimitSec: 45 },

        questions: [

          {

            id: `q${index}_1`,

            kind: 'multiple-choice',

            prompt: `¿Cuál afirmación sobre ${topic} es correcta?`,

            options: [

              `Concepto clave de ${topic}`,

              'Opción incorrecta A',

              'Opción incorrecta B',

              'Ninguna de las anteriores',

            ],

            correctIndex: 0,

          },

          {

            id: `q${index}_2`,

            kind: 'multiple-choice',

            prompt: `En el contexto de ${topic}, ¿qué elemento es fundamental?`,

            options: ['El proceso principal', 'Un detalle menor', 'Un mito común', 'No aplica'],

            correctIndex: 0,

          },

        ],

      });

      break;

    case 'completar': {

      const text = `El estudio de [${topic.split(' ')[0] ?? 'concepto'}] permite comprender [procesos] y [relaciones] en el tema.`;

      payload = validateCompletarPayload({

        title: label,

        text,

        blanks: parseCompletarBlanks(text),

        inputMode: index % 2 === 0 ? 'keyboard' : 'drag',

        distractors: ['elementos', 'causas', 'efectos'],

      });

      break;

    }

    case 'order':

      payload = validateActivityPayload('order', {

        title: label,

        prompt: `Ordena las etapas relacionadas con ${topic}`,

        items: [

          `Introducción a ${topic}`,

          'Desarrollo del concepto',

          'Aplicación práctica',

          'Conclusión y síntesis',

        ],

        correctOrder: [0, 1, 2, 3],

      });

      break;

    case 'vincula':

      payload = validateActivityPayload('vincula', {

        title: label,

        pairs: [

          { id: 'p1', ladoA: `Término A · ${topic}`, ladoB: 'Definición principal' },

          { id: 'p2', ladoA: 'Concepto clave', ladoB: 'Relación directa' },

          { id: 'p3', ladoA: 'Ejemplo', ladoB: 'Ilustración del tema' },

        ],

      });

      break;

    case 'desarrollo':

      payload = validateDesarrolloPayload({

        title: label,

        prompt: `Explica con tus palabras un aspecto central de ${topic}.`,

        rubric: ['Menciona al menos dos ideas', 'Usa vocabulario del tema', 'Organiza la respuesta'],

      });

      break;

    case 'agrupa':
      payload = validateActivityPayload('agrupa', {
        title: label,
        categories: [
          { id: 'c1', label: 'Conceptos' },
          { id: 'c2', label: 'Ejemplos' },
        ],
        items: [
          { id: 'i1', text: `Idea central de ${topic}`, categoryId: 'c1' },
          { id: 'i2', text: 'Definición complementaria', categoryId: 'c1' },
          { id: 'i3', text: 'Caso práctico', categoryId: 'c2' },
          { id: 'i4', text: 'Aplicación cotidiana', categoryId: 'c2' },
        ],
      });
      break;

    case 'sandbox':
      payload = validateActivityPayload('sandbox', {
        variant: 'balanza',
        title: label,
        targetValue: 12,
        initialValue: 0,
        maxValue: 24,
        unitLabel: 'kg',
        availableVariables: [
          { id: 'add2', label: '+ 2', internalValue: 2, type: 'ADD' },
          { id: 'add5', label: '+ 5', internalValue: 5, type: 'ADD' },
        ],
      });
      break;

    default:
      payload = validateActivityPayload('completar', {
        title: label,
        text: `En ${topic}, el concepto [central] se relaciona con [aplicación].`,
        inputMode: 'keyboard',
        distractors: ['detalle', 'proceso'],
      });
  }



  return {

    id: createBlockId(),

    index,

    mode,

    label,

    payload: payload as EvaluationBlock['payload'],

  };

}



export function generateExamFromOptions(options: ExamGenerateOptions): EvaluationExam {
  const topic = extractTopic(options.prompt);
  const count = options.blockCount ?? extractCount(options.prompt);
  const focus = options.focus ?? 'practical';
  const resourceType = options.resourceType === 'guide' ? 'guide' : 'exam';
  const modes = pickModes(count, focus, resourceType);
  const now = Date.now();

  const exam: EvaluationExam = {
    id: `exam_${now.toString(36)}`,
    teacherId: options.teacherId ?? 'teacher',
    title: resourceType === 'guide' ? `Guía: ${topic}` : `Evaluación: ${topic}`,
    subjectTag: options.subjectTag ?? 'general',
    resourceType,
    createdAt: now,
    updatedAt: now,

    settings: {

      shuffleBlocks: false,

      allowRejoin: true,

      integrityMode: 'STRICT',

      advanceMode: 'AUTONOMOUS',

    },

    blocks: modes.map((mode, i) => blockForTopic(mode, topic, i)),

  };



  return validateExam({ ...exam, blocks: reorderBlocksPedagogically(exam.blocks) });

}



export function generateExamFromPrompt(prompt: string, teacherId = 'teacher'): EvaluationExam {
  const topic = extractTopic(prompt);
  const count = extractCount(prompt);
  const balancedModes: GameMode[] = ['arena', 'completar', 'order', 'vincula', 'desarrollo'];
  const modes = Array.from({ length: count }, (_, i) => balancedModes[i % balancedModes.length]!);
  const now = Date.now();

  const exam: EvaluationExam = {
    id: `exam_${now.toString(36)}`,
    teacherId,
    title: `Evaluación: ${topic}`,
    subjectTag: 'general',
    createdAt: now,
    updatedAt: now,
    settings: {
      shuffleBlocks: false,
      allowRejoin: true,
      integrityMode: 'STRICT',
      advanceMode: 'AUTONOMOUS',
    },
    blocks: modes.map((mode, i) => blockForTopic(mode, topic, i)),
  };

  return validateExam({ ...exam, blocks: reorderBlocksPedagogically(exam.blocks) });
}

